import { useState, useEffect, useRef } from "react";
import * as XLSX from "xlsx";

// ─── Mock / constants ────────────────────────────────────────────────────────
const TEMPLATES_ENDPOINT = "/api/remarketing/templates";
const SEND_ENDPOINT = "/api/remarketing/send-template";

// ─── Helpers ─────────────────────────────────────────────────────────────────
function renderTemplateBody(template, sampleName = "Juan") {
  if (!template) return "";
  let text = template.body || "";
  // Replace {{1}} with sample name, {{2}} onwards with placeholders
  text = text.replace(/\{\{1\}\}/g, sampleName);
  text = text.replace(/\{\{(\d+)\}\}/g, (_, n) => `[variable ${n}]`);
  return text;
}

function formatPhoneNumber(num) {
  let s = String(num).replace(/\D/g, "");

  // Si viene con el prefijo de Colombia (+57 o 57), lo elimina
  if (s.length === 12 && (s.startsWith("57") || s.startsWith("+") || s.startsWith("5"))) {
    s = s.slice(2);
  }

  return s;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatusBadge({ type, children }) {
  const colors = {
    success: { bg: "#e6f4ea", text: "#2d6a4f", border: "#95d5b2" },
    error:   { bg: "#fde8e8", text: "#9b2335", border: "#f5a5a5" },
    warning: { bg: "#fff4e0", text: "#7b4f00", border: "#ffc947" },
    info:    { bg: "#e8f0fb", text: "#1a4a8a", border: "#a8c4ef" },
  };
  const c = colors[type] || colors.info;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      background: c.bg, color: c.text, border: `1px solid ${c.border}`,
      borderRadius: 20, padding: "3px 12px", fontSize: 12, fontWeight: 500,
    }}>
      {children}
    </span>
  );
}

/**
 * Extrae y normaliza los componentes de un template de WhatsApp.
 * Maneja excepciones devolviendo valores nulos o vacíos si el componente no existe.
 * * @param {Object} templateData - El objeto JSON completo del template
 * @returns {Object} Un objeto con propiedades predecibles (header, body, footer, buttons)
 */
function extractTemplateComponents(templateData) {
  // 1. Estructura base por defecto (evita undefined errors en el frontend)
  const result = {
    header: null,
    body: "",
    footer: "",
    buttons: []
  };

  // 2. Validación de seguridad: si no hay componentes, devolvemos la base vacía
  if (!templateData?.components || !Array.isArray(templateData.components)) {
    return result;
  }

  // 3. Iteramos de forma segura y asignamos según el tipo
  templateData.components.forEach((component) => {
    switch (component.type) {
      case "HEADER":
        result.header = {
          format: component.format || "TEXT",
          text: component.text || null, // Existirá si el formato es TEXT
          // Aquí puedes agregar campos extras que Meta a veces envía (como examples)
        };
        break;

      case "BODY":
        result.body = component.text || "";
        break;

      case "FOOTER":
        result.footer = component.text || "";
        break;

      case "BUTTONS":
        // Aseguramos que siempre sea un array, aunque venga mal formado
        result.buttons = Array.isArray(component.buttons) ? component.buttons : [];
        break;

      default:
        // Ignoramos silenciosamente tipos no reconocidos o futuros de la API
        break;
    }
  });

  return result;
}

function TemplatePreview({ template }) {
  console.log("Renderizando TemplatePreview con template:", template);
  if (!template) {

    return (
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", height: "100%", gap: 16,
        color: "#9ca3af",
      }}>
        <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
          <rect x="6" y="6" width="44" height="44" rx="12" fill="#f3f4f6"/>
          <path d="M18 22h20M18 28h14M18 34h10" stroke="#d1d5db" strokeWidth="2.5" strokeLinecap="round"/>
        </svg>
        <p style={{ margin: 0, fontSize: 14 }}>Selecciona una plantilla para previsualizarla</p>
      </div>
    );
  }

  // usa la funcion extractTemplateComponents para obtener header, body, footer y buttons de forma segura
    const { header, body, footer, buttons } = extractTemplateComponents(template);
    const { name, status, category, language } = template;
    console.log(template);
  const sampleBody = renderTemplateBody(template, "Carlos");

  console.log("Renderizando plantilla");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, height: "100%" }}>
      {/* Meta info */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        <StatusBadge type={status === "APPROVED" ? "success" : "warning"}>
          {status === "APPROVED" ? "✓ Aprobada" : status}
        </StatusBadge>
        <StatusBadge type="info">{category}</StatusBadge>
        <StatusBadge type="info">{language}</StatusBadge>
      </div>

      {/* Phone mockup */}
      <div style={{ display: "flex", justifyContent: "center", flex: 1 }}>
        <div style={{
          width: 280, background: "#f0f2f5", borderRadius: 20,
          border: "2px solid #d1d5db", overflow: "hidden",
          display: "flex", flexDirection: "column",
        }}>
          {/* Phone bar */}
          <div style={{
            background: "#075e54", color: "#fff",
            padding: "10px 16px", display: "flex", alignItems: "center", gap: 10,
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: "50%",
              background: "#25d366", display: "flex", alignItems: "center",
              justifyContent: "center", fontWeight: 700, fontSize: 14,
            }}>W</div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>WhatsApp</div>
              <div style={{ fontSize: 11, opacity: 0.8 }}>en línea</div>
            </div>
          </div>

          {/* Chat area */}
          <div style={{
            flex: 1, padding: "16px 12px", background: "#e5ddd5",
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60' opacity='0.05'%3E%3Cpath d='M0 0h60v60H0z' fill='%23000'/%3E%3C/svg%3E")`,
            minHeight: 320,
          }}>
            {/* Message bubble */}
            <div style={{
              background: "#fff", borderRadius: "0 12px 12px 12px",
              padding: 10, maxWidth: "90%", boxShadow: "0 1px 2px rgba(0,0,0,.13)",
              fontSize: 13, lineHeight: 1.5,
            }}>
              {/* Header */}
              {header && (
                <div style={{
                  fontWeight: 700, marginBottom: 6, fontSize: 14,
                  borderBottom: "1px solid #f0f0f0", paddingBottom: 6,
                }}>
                  {header.type === "IMAGE"
                    ? <div style={{
                        background: "#f0f2f5", borderRadius: 8, height: 100,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: "#9ca3af", fontSize: 12, marginBottom: 4,
                      }}>🖼 Imagen de cabecera</div>
                    : header.text}
                </div>
              )}

              {/* Body */}
              <div style={{ whiteSpace: "pre-wrap", color: "#111b21" }}>
                {body}
              </div>

              {/* Footer */}
              {footer && (
                <div style={{
                  marginTop: 6, paddingTop: 6, borderTop: "1px solid #f0f0f0",
                  color: "#667781", fontSize: 11,
                }}>
                  {footer}
                </div>
              )}

              {/* Timestamp */}
              <div style={{
                textAlign: "right", color: "#667781", fontSize: 10, marginTop: 4,
              }}>
                {new Date().toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit" })} ✓✓
              </div>
            </div>

            {/* Buttons */}
            {buttons && buttons.length > 0 && (
              <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4 }}>
                {buttons.map((btn, i) => (
                  <div key={i} style={{
                    background: "#fff", borderRadius: 8,
                    padding: "8px 12px", textAlign: "center",
                    color: "#00a5f4", fontWeight: 600, fontSize: 13,
                    boxShadow: "0 1px 2px rgba(0,0,0,.1)", cursor: "pointer",
                  }}>
                    {btn.type === "PHONE_NUMBER" ? "📞 " : btn.type === "URL" ? "🔗 " : ""}
                    {btn.text}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Template name */}
      <p style={{
        textAlign: "center", margin: 0, fontSize: 12,
        color: "#6b7280", fontFamily: "monospace",
      }}>
        {name}
      </p>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function MassWhatsAppPage() {
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [contacts, setContacts] = useState([]);
  const [fileName, setFileName] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);
  const [whatsappAccount, setWhatsappAccount] = useState(null);
  const [campaigns, setCampaigns] = useState([]);

  const selectedTemplate = templates.find((t) => t.id == selectedTemplateId) || null;

    // Configuración de la URL del servidor Socket.IO y API desde variables de entorno
    const socketUrl = process.env.REACT_APP_SOCKET_URL; // URL de tu servidor Socket.IO
    // const socket = io(socketUrl);
    const API_URL = process.env.REACT_APP_API_URL;

  // Fetch templates on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        // ── Replace this block with your real endpoint ──
        const res = await fetch(`${API_URL}remarketing/templates`);
        const data = await res.json();

        // convertir a json
        //const templates = JSON.parse(data);

        setTemplates(data);

        console.log("Templatesss cargadas:", data);

      } catch (error) {
        console.error("Error fetching templates:", error);
        setResult({ type: "error", message: "No se pudieron cargar las plantillas." });
      } finally {
        setLoadingTemplates(false);
      }
    };

    const fetchCampaigns = async () => {
        try {
            const response = await fetch(`${API_URL}remarketing/campaigns`);

            const data = await response.json();

            if (data.success) {
                setCampaigns(data.campaigns);
            }

            console.log(data);
        } catch (error) {
            console.error(error);
        }
    };


    fetchTemplates();
    fetchCampaigns();
  }, []);

  // Parse CSV or XLSX
  const handleFileChange = (e) => {
    console.log("Archivo seleccionado:", e.target.files[0]);
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    setContacts([]);
    setResult(null);

    const ext = file.name.split(".").pop().toLowerCase();

    if (ext === "csv") {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const text = ev.target.result;
        const rows = text.split(/\r?\n/).filter(Boolean).slice(1); // skip header
        const parsed = rows.map((row) => {
          const [nombre, numero] = row.split(",").map((c) => c.trim().replace(/"/g, ""));
          return { numero: formatPhoneNumber(numero), nombre };
        }).filter((c) => c.numero && c.nombre); 
        setContacts(parsed);
        console.log("Archivo procesado, contactos extraídos:", contacts);
      };
      reader.readAsText(file); // readAsText es suficiente para CSV y evita problemas de encoding que a veces da readAsBinaryString
    } else if (ext === "xlsx" || ext === "xls") {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const wb = XLSX.read(ev.target.result, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1 }).slice(1); // skip header
        const parsed = rows.map(([nombre, numero]) => ({
          numero: formatPhoneNumber(numero),
          nombre: String(nombre || ""),
        })).filter((c) => c.numero && c.nombre);
        setContacts(parsed);
        console.log("Archivo procesado, contactos extraídos:", contacts);
      };
      reader.readAsArrayBuffer(file); // XLSX recomienda readAsArrayBuffer para mejor compatibilidad con diferentes formatos de Excel
    } else {
      setResult({ type: "error", message: "Formato no soportado. Usa .csv o .xlsx" });
    }
    
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedTemplateId) {
      setResult({ type: "warning", message: "Selecciona una plantilla antes de enviar." });
      return;
    }
    if (contacts.length === 0) {
      setResult({ type: "warning", message: "Importa un archivo con contactos primero." });
      return;
    }

    if (!whatsappAccount) {
      setResult({ type: "warning", message: "Selecciona una cuenta de WhatsApp antes de enviar." });
      return; 
    }

    setSending(true);
    setResult(null);

    try {
      // ── Replace with your real endpoint call ──
      const formData = new FormData();
      formData.append("selectedTemplateId", selectedTemplateId);
      formData.append("contacts", JSON.stringify(contacts));
      formData.append("whatsappAccount", whatsappAccount); // Ajusta según tu lógica de cuentas
      // formData.append("prueba", "valor de prueba");
      if (file) {
        formData.append("file", file);
      }
      console.log("Enviando formData:", formData);
      const res = await fetch(`${API_URL}remarketing/send-template`, {
        method: "POST",
        headers: {
          // Nota: Al usar FormData con fetch, NO establezcas el Content-Type. 
          // El navegador lo configura automáticamente con el boundary correcto.
          'Accept': 'application/json',
          // 'Authorization': `Bearer ${token}` // Descomentar si usas tokens
        },
        body: formData, // Si envías FormData, no establezcas Content-Type, el navegador lo hará automáticamente con el boundary correcto
      });

      const data = await res.json();
      console.log("Respuesta del envío:", data);
      // Simulate API call
      // await new Promise((r) => setTimeout(r, 1800));

      // setResult({
      //   type: "success",
      //   message: `Envío iniciado correctamente. Se procesarán ${contacts.length} mensaje(s) con la plantilla "${selectedTemplate?.name}".`,
      // });

    } catch {
      setResult({ type: "error", message: "Error al enviar. Intenta de nuevo." });
    } finally {
      setSending(false);
    }
  };

  // Manejador específico para la imagen con generación de preview
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // setFormData((prev) => ({ ...prev, imagen: file }));
      // setImagePreview(URL.createObjectURL(file));
      setFile(file);
      console.log("Imagen seleccionada:", file);
    } else {
      // setFormData((prev) => ({ ...prev, imagen: null }));
      // setImagePreview(null);
      setFile(null);
    }
  };

  const handleUpdateTemplates = async () => {
    const fetchTemplates = async () => {
      setLoadingTemplates(true);
      try {
        const res = await fetch(`${API_URL}remarketing/sync-templates`, { method: "POST" });
        const data = await res.json();
        // setTemplates(data);
        console.log("Templates actualizadas:", data.data || data);
      } catch {
        setResult({ type: "error", message: "No se pudieron actualizar las plantillas." });
      } finally {
        setLoadingTemplates(false);
      }
    };
    await fetchTemplates();
  };


  const handleClearFile = () => {
    setContacts([]);
    setFileName("");
    if (fileInputRef.current) fileInputRef.current.value = "";
    setResult(null);
  };

  // ─── Styles ────────────────────────────────────────────────────────────────
  const S = {
    page: {
      minHeight: "100vh",
      background: "#f8fafc",
      fontFamily: "'Inter', 'Segoe UI', sans-serif",
      padding: "0 0 40px",
      width: "100%",
    },
    topBar: {
      background: "#fff",
      borderBottom: "1px solid #e5e7eb",
      padding: "0 32px",
      height: 60,
      display: "flex",
      alignItems: "center",
      gap: 12,
      marginBottom: 24,
    },
    waIcon: {
      width: 32, height: 32, borderRadius: 8,
      background: "#25d366",
      display: "flex", alignItems: "center", justifyContent: "center",
    },
    topTitle: { margin: 0, fontSize: 17, fontWeight: 600, color: "#111827" },
    topSub: { margin: 0, fontSize: 13, color: "#6b7280", marginLeft: "auto" },

    main: {
      maxWidth: "95%",
      margin: "auto",
      padding: "0 24px",
      display: "flex",
      gap: 24,
      alignItems: "flex-start",
    },

    // Left panel
    leftPanel: {
      flex: "0 0 400px",
      background: "#fff",
      borderRadius: 16,
      border: "1px solid #e5e7eb",
      padding: 28,
      display: "flex",
      flexDirection: "column",
      gap: 24,
    },
    panelTitle: {
      margin: 0, fontSize: 15, fontWeight: 600, color: "#111827",
      display: "flex", alignItems: "center", gap: 8,
    },
    label: {
      display: "block", fontSize: 13, fontWeight: 500,
      color: "#374151", marginBottom: 6,
    },
    select: {
      width: "100%", padding: "10px 12px",
      border: "1px solid #d1d5db", borderRadius: 10,
      fontSize: 14, color: "#111827",
      background: "#fff", cursor: "pointer",
      outline: "none", appearance: "none",
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "right 10px center",
      paddingRight: 36,
    },
    dropzone: {
      border: "2px dashed #d1d5db",
      borderRadius: 12,
      padding: "24px 16px",
      textAlign: "center",
      cursor: "pointer",
      background: "#f9fafb",
      transition: "border-color .2s",
    },
    dropzoneActive: {
      border: "2px dashed #25d366",
      background: "#f0fdf4",
    },
    uploadIcon: {
      width: 40, height: 40, borderRadius: 10,
      background: "#f3f4f6",
      display: "flex", alignItems: "center", justifyContent: "center",
      margin: "0 auto 10px",
    },
    chip: {
      display: "inline-flex", alignItems: "center", gap: 6,
      background: "#f0fdf4", color: "#166534",
      border: "1px solid #86efac",
      borderRadius: 20, padding: "4px 12px",
      fontSize: 12, fontWeight: 500,
    },
    closeBtn: {
      background: "none", border: "none", cursor: "pointer",
      color: "#6b7280", fontSize: 14, padding: "0 2px",
      display: "flex", alignItems: "center",
    },
    table: {
      width: "100%", borderCollapse: "collapse",
      fontSize: 12, color: "#374151",
    },
    th: {
      background: "#f9fafb", padding: "6px 10px",
      textAlign: "left", borderBottom: "1px solid #e5e7eb",
      fontWeight: 600, color: "#6b7280", textTransform: "uppercase",
      fontSize: 11, letterSpacing: "0.04em",
    },
    td: {
      padding: "6px 10px", borderBottom: "1px solid #f3f4f6",
    },
    sendBtn: {
      width: "100%", padding: "13px",
      background: sending ? "#9ca3af" : "#25d366",
      color: "#fff", border: "none", borderRadius: 12,
      fontSize: 15, fontWeight: 600, cursor: sending ? "not-allowed" : "pointer",
      display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
      transition: "background .2s, transform .1s",
      letterSpacing: "0.01em",
    },
    alertBox: (type) => {
      const map = {
        success: { bg: "#f0fdf4", border: "#86efac", text: "#166534" },
        error:   { bg: "#fef2f2", border: "#fca5a5", text: "#991b1b" },
        warning: { bg: "#fffbeb", border: "#fcd34d", text: "#92400e" },
      };
      const c = map[type] || map.error;
      return {
        background: c.bg, border: `1px solid ${c.border}`,
        color: c.text, borderRadius: 10, padding: "12px 14px",
        fontSize: 13, lineHeight: 1.5,
      };
    },

    // Right panel
    rightPanel: {
      flex: 1,
      background: "#fff",
      borderRadius: 16,
      border: "1px solid #e5e7eb",
      padding: 28,
      minHeight: 540,
      display: "flex",
      flexDirection: "column",
      gap: 16,
    },
    divider: { border: "none", borderTop: "1px solid #f3f4f6", margin: "0" },
  


    campaignsCard: {
  background: "#fff",
  borderRadius: "16px",
  boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
  overflow: "hidden",
  marginTop: "20px",
},

cardHeader: {
  padding: "20px",
  borderBottom: "1px solid #eee",
},

tableContainer: {
  overflowX: "auto",
  overflowY: 'auto'
},

tableCampaign : {
  width: "100%",
  borderCollapse: "collapse",
  minWidth: "1600px",
  margin : '2rem'
},

statusBadge: {
  padding: "6px 12px",
  borderRadius: "999px",
  fontSize: "12px",
  fontWeight: 600,
  textTransform: "capitalize",
},

statusCompleted: {
  background: "#dcfce7",
  color: "#166534",
},

statusFailed: {
  background: "#fee2e2",
  color: "#991b1b",
},

statusRunning: {
  background: "#dbeafe",
  color: "#1e40af",
},
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      {/* Top bar */}
      <div style={S.topBar}>
        <div style={S.waIcon}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
            <path d="M11.998 2C6.476 2 2 6.477 2 12.001c0 1.767.462 3.43 1.27 4.878L2 22l5.263-1.376A9.956 9.956 0 0011.998 22C17.521 22 22 17.523 22 12c0-5.522-4.479-10-10.002-10zm0 18.182a8.167 8.167 0 01-4.168-1.141l-.3-.178-3.122.817.832-3.044-.196-.313A8.186 8.186 0 013.82 12c0-4.509 3.672-8.181 8.178-8.181 4.508 0 8.181 3.672 8.181 8.181 0 4.508-3.673 8.182-8.181 8.182z"/>
          </svg>
        </div>
        <h1 style={S.topTitle}>Envío masivo WhatsApp</h1>
        <p style={S.topSub}>Meta Cloud API · Plantillas aprobadas</p>
      </div>

      {/* Main content */}
      <div style={S.main}>

        {/* ── LEFT: Form ── */}
        <div style={S.leftPanel}>
          <p style={S.panelTitle}>
            <span style={{ fontSize: 18 }}>⚙️</span> Configurar envío
            <button style={S.button} onClick={handleUpdateTemplates}>
              Actualizar
            </button>
          </p>

          <hr style={S.divider} />

          {/* WhatsApp Account select */}
          <div>
            <label style={S.label}>Cuenta de WhatsApp</label>
            <select
              style={S.select}
              value={whatsappAccount || ""}
              onChange={(e) => setWhatsappAccount(e.target.value)}
            >
              <option value="">— Seleccionar Numero —</option>
              {/* <option value="1090063357514431">API PC PRUEBAS</option> */}
              <option value="1071577299369584">CHAT PRINCIPAL - 315 984 3783</option>
              <option value="1148650718332629">REMARKETING - 318 4904838</option>
              <option value="1646529648534427">UTILIDAD - 318 2576099</option>
            </select>
          </div>
          



          {/* Template select */}
          <div>
            <label style={S.label}>Plantilla aprobada</label>
            {loadingTemplates ? (
              <div style={{
                height: 40, background: "#f3f4f6", borderRadius: 10,
                display: "flex", alignItems: "center", paddingLeft: 14,
                fontSize: 13, color: "#9ca3af",
              }}>
                Cargando plantillas…
              </div>
            ) : (
              <select
                style={S.select}
                value={selectedTemplateId}
                onChange={(e) => {
                  setSelectedTemplateId(e.target.value);
                  setResult(null);
                }}
              >
                <option value="">— Seleccionar plantilla —</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.category})
                  </option>
                ))}
              </select>
            )}
            {selectedTemplate && (
              <p style={{ margin: "6px 0 0", fontSize: 12, color: "#6b7280" }}>
                Idioma: {selectedTemplate.language} · Estado:{" "}
                <span style={{ color: "#16a34a", fontWeight: 500 }}>
                  {selectedTemplate.status}
                </span>
              </p>
            )}
          </div>

          {selectedTemplate?.has_image === 1 && (
            <div>
              <label>Imagen:</label>
              <input 
              type="file" 
              accept="image/*" 
              onChange={handleImageChange} 
              />
            </div>
          )}

          {/* File import */}
          <div>
            <label style={S.label}>Importar contactos</label>

            {contacts.length === 0 ? (
              <div
                style={S.dropzone}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const file = e.dataTransfer.files[0];
                  if (file) {
                    const fakeEv = { target: { files: [file] } };
                    handleFileChange(fakeEv);
                  }
                }}
              >
                <div style={S.uploadIcon}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round">
                    <polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/>
                    <path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3"/>
                  </svg>
                </div>
                <p style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 500, color: "#374151" }}>
                  Arrastra tu archivo aquí
                </p>
                <p style={{ margin: 0, fontSize: 12, color: "#9ca3af" }}>
                  .CSV o .XLSX · columnas: <code style={{ background: "#f3f4f6", padding: "1px 5px", borderRadius: 4 }}>numero</code> y <code style={{ background: "#f3f4f6", padding: "1px 5px", borderRadius: 4 }}>nombre</code>
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
              </div>
            ) : (
              <div>
                {/* File chip */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <div style={S.chip}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    {fileName} · {contacts.length} contactos
                  </div>
                  <button style={S.closeBtn} onClick={handleClearFile} title="Quitar archivo">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>

                {/* Preview table */}
                <div style={{
                  border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden",
                  maxHeight: 160, overflowY: "auto",
                }}>
                  <table style={S.table}>
                    <thead>
                      <tr>
                        <th style={S.th}>#</th>
                        <th style={S.th}>Número</th>
                        <th style={S.th}>Nombre</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contacts.slice(0, 50).map((c, i) => (
                        <tr key={i}>
                          <td style={{ ...S.td, color: "#9ca3af" }}>{i + 1}</td>
                          <td style={S.td}>{c.numero}</td>
                          <td style={S.td}>{c.nombre}</td>
                        </tr>
                      ))}
                      {contacts.length > 50 && (
                        <tr>
                          <td colSpan={3} style={{ ...S.td, textAlign: "center", color: "#9ca3af", fontStyle: "italic" }}>
                            y {contacts.length - 50} más…
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Result alert */}
          {result && (
            <div style={S.alertBox(result.type)}>
              {result.message}
            </div>
          )}

          {/* Send button */}
          <button
            style={S.sendBtn}
            onClick={handleSubmit}
            disabled={sending}
          >
            {sending ? (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"
                  style={{ animation: "spin 1s linear infinite" }}>
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                </svg>
                Procesando…
              </>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round">
                  <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                </svg>
                Enviar {contacts.length > 0 ? `(${contacts.length})` : "mensajes"}
              </>
            )}
          </button>
        </div>

        {/* ── RIGHT: Preview ── */}
        <div style={S.rightPanel}>
          <p style={S.panelTitle}>
            <span style={{ fontSize: 18 }}>📱</span> Vista previa del mensaje
          </p>
          <hr style={S.divider} />
          <div style={{ flex: 1 }}>
            <TemplatePreview template={selectedTemplate} />
          </div>
        </div>
        
        

      </div>

      {/* Campaigns List */}
        {/* <div style={S.campaignsList}>
          <h3>Campañas</h3>
          <ul>
            {campaigns.map(c => (
              <li key={c.id}>{c.name}</li>
            ))}
          </ul>
        </div> */}
        {/* Campaigns List */}
<div style={S.campaignsCard}>
  <div style={S.cardHeader}>
    <h3 style={{ margin: 0 }}>Campañas</h3>
  </div>

  <div style={S.tableContainer}>
    <table style={S.tableCampaign}>
      <thead>
        <tr>
          <th>ID</th>
          <th>Nombre</th>
          <th>Descripción</th>
          {/* <th>Estado</th> */}
          <th>Total</th>
          <th>Procesados</th>
          <th>Exitosos</th>
          <th>Fallidos</th>
          <th>Plantilla</th>
          {/* <th>Creado por</th>
          <th>Inicio</th>
          <th>Finalización</th> */}
          <th>Creado</th>
          <th>Actualizado</th>
        </tr>
      </thead>

      <tbody>
        {campaigns.map((c) => (
          <tr key={c.id}>
            <td>{c.id}</td>
            <td>{c.name}</td>
            <td>{c.description}</td>
            {/* <td>
              <span
                style={{
                  ...S.statusBadge,
                  ...(c.job_status === "completed"
                    ? S.statusCompleted
                    : c.job_status === "failed"
                    ? S.statusFailed
                    : S.statusRunning),
                }}
              >
                {c.job_status}
              </span>
            </td> */}
            <td>{c.total_contacts}</td>
            <td>{c.processed_contacts}</td>
            <td>{c.successful_contacts}</td>
            <td>{c.failed_contacts}</td>
            <td>{c.template_name}</td>
            {/* <td>{c.created_by}</td>
            <td>{c.started_at}</td>
            <td>{c.completed_at}</td> */}
            <td>{c.created_at}</td>
            <td>{c.updated_at}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
</div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        select:focus { outline: 2px solid #25d366; border-color: transparent; }
      `}</style>
    </div>
  );
}